<template>
    <div class="container">
        <div class="pt-5">
            <h1 class="h3">Settings</h1>
            <div class="py-2">
                <settings-menu></settings-menu>
            </div>

            <div class="row py-3 border-bottom">
                <div class="col-md-6 col-sm-12">
                    <h3 class="h3 text-primary">Loyalty Program</h3>
                    <small>Reward customers with points on completed sales, redeemable for a discount at checkout.</small>
                </div>
                <div class="col-md-6 col-sm-12" v-if="hasEntitlement">
                    <div class="float-right">
                        <button @click="save" class="btn btn-primary px-3" :disabled="saving">
                            <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="!loaded" class="py-5 text-center text-muted">
                <span class="spinner-border spinner-border-sm mr-1"></span> Loading…
            </div>

            <!-- ── Not entitled: same premium-feature state pattern used elsewhere ── -->
            <div v-else-if="!hasEntitlement" class="container text-left mb-5">
                <div class="alert alert-secondary">
                    <strong>Loyalty is a premium feature.</strong>
                    <p class="mb-0 mt-1">
                        Enable it from <a href="/settings/features">Settings → Features</a> to configure your loyalty program.
                    </p>
                </div>
            </div>

            <div v-else class="container text-left mb-5">
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Enable Loyalty</h6>
                        <small>Turn the loyalty program on or off. Existing points/history are preserved either way.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="loyalty-enabled" v-model="program.is_enabled">
                            <label class="custom-control-label" for="loyalty-enabled"><small>Enable</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Earning</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Earning rate</h6>
                        <small>How many points a customer earns per amount spent. e.g. KES 100 = 1 point.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="d-flex align-items-center" style="gap:8px;">
                            <span>KES</span>
                            <input type="number" min="0.01" step="0.01" class="form-control" style="max-width:120px;" v-model.number="program.earn_rate_amount">
                            <span>spent =</span>
                            <input type="number" min="0.01" step="0.01" class="form-control" style="max-width:100px;" v-model.number="program.earn_rate_points">
                            <span>points</span>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Minimum qualifying amount</h6>
                        <small>Orders below this amount earn no points. Leave blank for no minimum.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input type="number" min="0" step="0.01" class="form-control" style="max-width:160px;" v-model.number="program.min_qualifying_amount" placeholder="No minimum">
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Maximum points per transaction</h6>
                        <small>Caps how many points a single sale can earn. Leave blank for no cap.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input type="number" min="1" step="1" class="form-control" style="max-width:160px;" v-model.number="program.max_points_per_transaction" placeholder="No cap">
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Rounding</h6>
                        <small>How fractional points are rounded when a sale earns a non-whole number of points.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="form-control" style="max-width:160px;" v-model="program.rounding_mode">
                            <option value="down">Round down</option>
                            <option value="nearest">Round to nearest</option>
                            <option value="up">Round up</option>
                        </select>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Points expiry</h6>
                        <small class="text-muted">Coming soon — points do not expire yet, regardless of this value.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input type="number" min="1" step="1" class="form-control" style="max-width:160px;" v-model.number="program.points_expire_after_days" placeholder="Never" disabled>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Redemption</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Redemption rate</h6>
                        <small>How much a discount points are worth at checkout. e.g. 100 points = KES 100 discount.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="d-flex align-items-center" style="gap:8px;">
                            <input type="number" min="1" step="1" class="form-control" style="max-width:100px;" v-model.number="program.redemption_rate_points">
                            <span>points = KES</span>
                            <input type="number" min="0.01" step="0.01" class="form-control" style="max-width:120px;" v-model.number="program.redemption_rate_value">
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Minimum redemption</h6>
                        <small>The fewest points a customer can redeem at once. Leave blank for no minimum.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input type="number" min="0" step="1" class="form-control" style="max-width:160px;" v-model.number="program.min_redemption_points" placeholder="No minimum">
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Require customer code to redeem</h6>
                        <small>The customer gets an SMS code that the cashier must enter before points come off. Stops points being spent without the customer.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="loyalty-redeem-otp" v-model="program.require_redemption_otp">
                            <label class="custom-control-label" for="loyalty-redeem-otp"><small>Require code</small></label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "loyalty-settings",
    data() {
        return {
            loaded: false,
            hasEntitlement: true,
            saving: false,
            program: {
                is_enabled: false,
                earn_rate_amount: 100,
                earn_rate_points: 1,
                redemption_rate_points: 100,
                redemption_rate_value: 100,
                min_qualifying_amount: null,
                min_redemption_points: null,
                max_points_per_transaction: null,
                require_redemption_otp: true,
                rounding_mode: 'down',
                points_expire_after_days: null,
            },
        }
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            axios.get('/api/loyalty/program')
                .then(result => {
                    this.program = { ...this.program, ...result.data }
                    this.hasEntitlement = true
                    this.loaded = true
                })
                .catch(e => {
                    if (e.response && e.response.status === 403) {
                        this.hasEntitlement = false
                    } else {
                        this.$toasts.push({ type: 'error', message: 'Failed to load loyalty settings.' })
                    }
                    this.loaded = true
                });
        },
        save() {
            this.saving = true
            axios.put('/api/loyalty/program', this.program)
                .then(result => {
                    this.program = { ...this.program, ...result.data }
                    this.$toasts.push({ type: 'success', message: 'Loyalty settings saved.' })
                })
                .catch(e => {
                    const msg = e.response?.data?.message || 'Failed to save loyalty settings.'
                    this.$toasts.push({ type: 'error', message: msg })
                })
                .finally(() => { this.saving = false })
        },
    },
}
</script>

<style scoped>
</style>
